import { Component, OnDestroy, OnInit } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { environment } from '../../../../environments/environment';
import { ApplicationStateService } from '../../../services/application-state.service';
import { CommunicationService } from '../../../services/communication.service';
import { Subscription } from 'rxjs';
import { ZoomService } from '../../../services/zoom.service';
import { ZoomEvent } from '../../../utils/events/zoom-event';
import { AssetService } from '../../../services/asset.service';
import { NgStyle } from '@angular/common';

@Component({
    selector: 'app-zoom-slider',
    templateUrl: './zoom-slider.component.html',
    styleUrls: ['./zoom-slider.component.scss'],
    standalone: true,
    imports: [
        NgStyle
    ]
})
export class ZoomSliderComponent implements OnInit, OnDestroy {

    minValue: number;
    stepSize: number;
    maxValue: number;
    currentZoomLevel: number;

    isIOSSafari: boolean;
    isMicrosoftEdge: boolean;
    isIE: boolean;

    private zoomLevelChangedsubscription: Subscription;
    private viewModeChangedSubscription: Subscription;
    private slider;

    constructor(public applicationStateService: ApplicationStateService,
                public zoomService: ZoomService,
                private communicationService: CommunicationService,
                private deviceService: DeviceDetectorService,
                public assetService: AssetService) {

        this.zoomLevelChangedsubscription = this.communicationService.zoomLevelChangedAnnouncedSource$.subscribe(
            event => this.onZoomLevelChange(event)
        );

        this.viewModeChangedSubscription = this.communicationService.viewModeChangeAnnounced$.subscribe(() => {
            const newLevel = this.applicationStateService.zoomLevel - environment.minZoomLevel;
            this.currentZoomLevel = Math.floor(newLevel / this.stepSize);
            this.getSlider().val(this.currentZoomLevel);
        });
    }

    /**
     * Called after component creation.
     */
    ngOnInit(): void {
        this.stepSize = (environment.maxZoomLevel - environment.minZoomLevel) / environment.amountZoomLevels;
        this.minValue = environment.minZoomLevel;
        this.maxValue = environment.amountZoomLevels;
        this.currentZoomLevel = this.applicationStateService.zoomLevel;

        const deviceInfo = this.deviceService.getDeviceInfo();
        this.isIE = deviceInfo.browser === 'ie';
        this.isIOSSafari = deviceInfo.browser === 'safari' && deviceInfo.device === 'iphone';
        this.isMicrosoftEdge = deviceInfo.browser === 'ms-edge';
    }

    /**
     * Called before component deletion.
     */
    ngOnDestroy(): void {
        this.zoomLevelChangedsubscription.unsubscribe();
        this.viewModeChangedSubscription.unsubscribe();
    }

    /**
     * Handler for zoom level change.
     *
     * @param {ZoomEvent} event
     */
    onZoomLevelChange(event: ZoomEvent): void {
        const newLevel = event.zoomLevel - environment.minZoomLevel;
        this.currentZoomLevel = Math.floor(newLevel / this.stepSize);
        this.getSlider().val(this.currentZoomLevel);
    }

    /**
     * Handler for tab/touchmove devices to support dragging/taping
     * of zoom slider.
     *
     * @param {Event} $event
     */
    onTap($event): void {
        let touchOffsetY: number;
        if ($event.center) {
            touchOffsetY = $event.center.y;
        } else if ($event.touches && $event.touches.length >= 0) {
            touchOffsetY = $event.touches[0].screenY;
        }

        if (touchOffsetY) {
            const sliderWidth: number = this.getSlider().width();
            const sliderMax: number = this.getSlider().attr('max');
            const sliderOffsetTop: number = this.getSlider().offset().top;

            const newValue: number = (sliderMax - ((sliderMax / sliderWidth) * (touchOffsetY - sliderOffsetTop)));
            this.changeZoom(newValue);
        }
    }

    /**
     * Handler for input range to change zoom value.
     *
     * @param {number} value
     * @param {Event} $event
     */
    changeZoom(value: number, $event?): void {
        if ($event) {
            $event.stopPropagation();
            $event.preventDefault();
        }

        if (value <= this.minValue) {
            this.zoomService.zoomMaxOut();

            return;
        }

        if (value >= this.maxValue) {
            this.zoomService.zoomMaxIn();

            return;
        }

        const newZoomLevel = environment.minZoomLevel + value * this.stepSize;
        this.zoomService.zoomToLevel(newZoomLevel);
    }

    /**
     * Because the Input-Event does not work on IE.
     * @param {number} value
     * @param $event
     */
    changeZoomForIE(value: number, $event?): void {

        if (this.isIE) {
            this.changeZoom(value, event);
        }
    }

    /**
     * Handler for zoom-in button click.
     *
     * @param {Event} $event
     */
    zoomIn($event): void {
        $event.stopPropagation();

        const currentZoomLevel = this.applicationStateService.zoomLevel;
        const percentageOfZoomIn = 0.25;
        const zoomInDelta = (environment.maxZoomLevel - environment.minZoomLevel) * percentageOfZoomIn;
        const newZoomLevel = currentZoomLevel + zoomInDelta;
        this.zoomService.zoomToLevel(newZoomLevel);
    }

    /**
     * Handler for zoom-out button click.
     *
     * @param {Event} $event
     */
    zoomOut($event): void {
        $event.stopPropagation();

        const currentZoomLevel = this.applicationStateService.zoomLevel;
        const percentageOfZoomIn = 0.25;
        const zoomInDelta = (environment.maxZoomLevel - environment.minZoomLevel) * percentageOfZoomIn;
        const newZoomLevel = currentZoomLevel - zoomInDelta;
        this.zoomService.zoomToLevel(newZoomLevel);
    }

    /**
     * Prevents changing the slider with arrow keys.
     *
     * @param {Event} $event
     */
    onKeyDown($event): void {
        $event.preventDefault();
    }

    private getSlider(): any {
        if (!this.slider) {
            this.slider = $('.zoom-range input[type=range]');
        }

        return this.slider;
    }
}
